import { QuestionEntity } from './question.entity';
import { QuestionDto, SportDefinition } from '@smatchy/sdk';
import { KeyValueCoordinateScoreValue, RunningBenchmarkPoint } from './sport-question';
import type { AScoreReference, ScoreTable, TerrainBand } from './what-a-score-reference-holds';

export class SportEntity {
  private questionEntity: QuestionEntity;
  private lastPercentile: number | null = null;

  private constructor(private readonly props: SportDefinition) {
    this.questionEntity = QuestionEntity.create(props.initialQuestion);
  }

  /**
   * The sport's database id.
   *
   * ASSERTED, AND HERE IS WHY. The generated SportDefinition declares
   * `_id?: string`, because the DTO carries no @ApiProperty for it, and every
   * definition this entity is built from was fetched from the API, which
   * writes it. The alternative is `string | undefined` reaching the survey,
   * which would then have to invent an answer for a sport that cannot exist:
   * either refuse to save a level somebody has just spent a screen earning, or
   * save it against nothing. Naming the assumption beats both.
   */
  get id(): string {
    return this.props._id as string;
  }

  static create(props: SportDefinition) {
    return new SportEntity(props);
  }

  getQuestion(): QuestionEntity {
    return this.questionEntity;
  }

  answerSpecificQuestion(value: string | number, question: QuestionDto): QuestionEntity {
    this.questionEntity.answer(value, question);
    return this.questionEntity;
  }

  answerQuestion(value: string): QuestionEntity {
    this.questionEntity.answer(value);
    return this.questionEntity;
  }

  /**
   * The level this answer earns, or null when it cannot be worked out.
   *
   * NULL IS NOT NEW, only declared. The percentile and terrainCoordinate
   * branches have always returned null when the distance or the time is
   * missing, and getCoordinateLevel returns null when the table has no band for
   * that pair. The signature said `number`, so a caller reading it was told a
   * level always comes back. The one caller, the survey, already tested for it.
   */
  getScoreFromRefer(scoreReferId: string, sex?: string): number | null {
    // `?? []`: the generated sport declares its references optional, and a
    // sport that carries none has no level to give -- which is the null below.
    const scoreRefer = (this.props.scoreRefers ?? []).find((s) => s.id === scoreReferId);
    if (scoreRefer) {
      switch (scoreRefer.type as string) {
        case 'keyValue': {
          // Through AScoreReference like every other branch: the generated
          // ScoreRefer declares `values?: object`, so the table may be absent
          // and cannot be indexed as declared. An answer the table does not
          // list answers null, which is what an undefined lookup already meant
          // to the one caller -- it tests for both.
          const table = (scoreRefer as AScoreReference).values;
          const level = table?.[String(this.questionEntity.getCurrentAnswer())];

          return typeof level === 'number' ? level : null;
        }

        case 'area': {
          const answer = this.questionEntity.getCurrentAnswer();
          if (this.questionEntity.getCurrentQuestion().type === 'number') {
            const values = scoreRefer.values as KeyValueCoordinateScoreValue[];
            const found = values.find((v: KeyValueCoordinateScoreValue) =>
              this.between(v.from, v.to, parseInt(answer.toString(), 10)),
            );
            if (found) {
              return found.level;
            }
          } else if (this.questionEntity.getCurrentQuestion().type === 'float') {
            const values = scoreRefer.values as KeyValueCoordinateScoreValue[];
            const found = values.find((v: KeyValueCoordinateScoreValue) =>
              this.between(v.from, v.to, parseFloat(answer.toString())),
            );
            if (found) {
              return found.level;
            }
          }
          return 1;
        }

        case 'coordinate': {
          const answers = this.getReferenceAnswers();
          return this.getCoordinateLevel((scoreRefer as AScoreReference).values, answers.distance, answers.time);
        }

        case 'percentile': {
          const answers = this.getReferenceAnswers();
          if (!answers.distance || !answers.time) {
            return null;
          }
          const percentile = this.getRunningPercentile(
            (scoreRefer as AScoreReference).values,
            answers.distance,
            answers.time,
            sex,
          );
          this.lastPercentile = percentile;
          return this.getLevelFromPercentile(percentile);
        }

        case 'terrainCoordinate': {
          const answers = this.getReferenceAnswers();
          if (!answers.distance || !answers.time) {
            return null;
          }
          const terrainBand = this.getTerrainBand(
            (scoreRefer as AScoreReference).terrainBands,
            answers.distance,
            answers.elevation || 0,
          );
          return this.getCoordinateLevel(
            (scoreRefer as AScoreReference).values,
            `${terrainBand}.${answers.distance}`,
            answers.time,
          );
        }
      }
    }
    return 1;
  }

  getMaxLevel() {
    return this.props?.maxLevel || 10;
  }

  getLastPercentile(): number | null {
    return this.lastPercentile;
  }

  private between(a: number, b: number, v: number, inclusive = true) {
    const min = Math.min.apply(Math, [a, b]);
    const max = Math.max.apply(Math, [a, b]);
    return inclusive ? v >= min && v <= max : v > min && v < max;
  }

  private getReferenceAnswers(): { distance?: string; time?: number; elevation?: number } {
    const answers = Array.from(this.questionEntity.getHistoricAnswers().values()).map((answer) => answer.value);
    const distance = answers.find(
      (answer): answer is string => typeof answer === 'string' && this.isReferenceDistance(answer),
    );
    const numericAnswers = answers
      .map((answer) => this.toNumber(answer))
      .filter((answer): answer is number => answer !== undefined);

    return {
      distance,
      time: numericAnswers[0],
      elevation: numericAnswers[1],
    };
  }

  /**
   * Distance and time are optional here, and were already being passed absent.
   *
   * The `coordinate` branch hands over whatever getReferenceAnswers found,
   * without the guard the `percentile` branch puts in front of the same call.
   * An absent distance indexes nothing and an absent time fails the test below,
   * so both already answered null; the parameters now say so.
   */
  private getCoordinateLevel(
    values: ScoreTable | undefined,
    distance: string | undefined,
    time: number | undefined,
  ): number | null {
    const coordinateScoreValues = values?.[distance ?? ''] as KeyValueCoordinateScoreValue[] | undefined;
    if (!coordinateScoreValues || !time) {
      return null;
    }

    const found = coordinateScoreValues.find((v: KeyValueCoordinateScoreValue) => this.between(v.from, v.to, time));
    return found?.level ?? null;
  }

  private getRunningPercentile(values: ScoreTable | undefined, distance: string, time: number, sex?: string): number {
    // A ScoreTable is `Record<string, unknown>`, so the first index answers
    // `unknown` and cannot be indexed again. The shape of the second level is
    // named here rather than asserted at each of the two reads.
    const byDistance = (gender: string): Record<string, RunningBenchmarkPoint[]> | undefined =>
      values?.[gender] as Record<string, RunningBenchmarkPoint[]> | undefined;

    const benchmark: RunningBenchmarkPoint[] =
      byDistance(this.normalizeGender(sex))?.[distance] || byDistance('neutral')?.[distance] || [];

    if (!benchmark.length) {
      return 0;
    }

    const sortedBySlowest = [...benchmark].sort((a, b) => b.time - a.time);
    if (time >= sortedBySlowest[0].time) {
      return sortedBySlowest[0].percentile;
    }

    const sortedByFastest = [...benchmark].sort((a, b) => a.time - b.time);
    if (time <= sortedByFastest[0].time) {
      return sortedByFastest[0].percentile;
    }

    for (let i = 0; i < sortedBySlowest.length - 1; i++) {
      const slower = sortedBySlowest[i];
      const faster = sortedBySlowest[i + 1];

      if (time <= slower.time && time >= faster.time) {
        const progress = (slower.time - time) / (slower.time - faster.time);
        return Math.round((slower.percentile + progress * (faster.percentile - slower.percentile)) * 10) / 10;
      }
    }

    return 0;
  }

  private getLevelFromPercentile(percentile: number): number {
    if (percentile >= 95) {
      return 5;
    }
    if (percentile >= 75) {
      return 4;
    }
    if (percentile >= 50) {
      return 3;
    }
    if (percentile >= 25) {
      return 2;
    }
    return 1;
  }

  private getTerrainBand(terrainBands: TerrainBand[] | undefined, distance: string, elevation: number): string {
    const distanceKm = parseInt(distance, 10);
    const elevationPerKm = distanceKm > 0 ? elevation / distanceKm : 0;
    const terrainBand = terrainBands?.find((band) => {
      const underUpperBound = band.to === undefined || elevationPerKm < band.to;
      return elevationPerKm >= band.from && underUpperBound;
    });

    return terrainBand?.id || 'flat';
  }

  private normalizeGender(sex?: string): string {
    // `?? ''` rather than a non-null assertion: sex is optional at the call
    // site, an absent one is not male or female, and '' falls to neutral below,
    // which is what the function already did.
    const value = sex?.toLowerCase()?.trim() ?? '';
    if (['male', 'man', 'm', 'homme', 'masculin'].includes(value)) {
      return 'male';
    }
    if (['female', 'woman', 'f', 'femme', 'feminin', 'féminin'].includes(value)) {
      return 'female';
    }
    return 'neutral';
  }

  private toNumber(answer: unknown): number | undefined {
    if (typeof answer === 'number') {
      return Number.isFinite(answer) ? answer : undefined;
    }

    if (typeof answer === 'string') {
      const value = Number(answer);
      return Number.isFinite(value) ? value : undefined;
    }

    return undefined;
  }

  private isReferenceDistance(answer: unknown): answer is string {
    return typeof answer === 'string' && ['10k', 'marathon', '20km', '30km', '60km', '100km', '180km'].includes(answer);
  }
}
